import { Link } from '@inertiajs/react'
import { cn } from '@/lib/utils'

interface CTAButtonProps {
  variant?: 'primary' | 'secondary' | 'outline'
  size?: 'sm' | 'md' | 'lg'
  href?: string
  children: React.ReactNode
  className?: string
  onClick?: () => void
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
}

function isExternalUrl(url: string) {
  return url.startsWith('http://') || url.startsWith('https://')
}

const sizeClasses = {
  sm: 'h-9 px-4 text-sm',
  md: 'h-11 px-6 text-base',
  lg: 'h-14 px-8 text-lg',
}

const variantClasses = {
  primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
  secondary:
    'bg-secondary text-secondary-foreground hover:bg-secondary/90 border border-secondary',
  outline:
    'border-2 border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground',
}

export function CTAButton({
  variant = 'primary',
  size = 'md',
  href,
  children,
  className,
  onClick,
  disabled,
  type = 'button',
}: CTAButtonProps) {
  const combinedClassName = cn(
    'inline-flex items-center justify-center',
    'font-semibold rounded-full transition-colors',
    'disabled:opacity-50 disabled:pointer-events-none',
    'aria-disabled:opacity-50 aria-disabled:pointer-events-none',
    sizeClasses[size],
    variantClasses[variant],
    className,
  )

  if (href) {
    const external = isExternalUrl(href)
    // <a> elements can't be HTML-disabled; mirror the source's disabled styling
    // via aria-disabled (which our Tailwind classes target).
    return (
      <Link
        href={href}
        className={combinedClassName}
        aria-disabled={disabled || undefined}
        {...(external && { target: '_blank', rel: 'noopener noreferrer' })}
      >
        {children}
      </Link>
    )
  }

  return (
    <button
      type={type}
      className={combinedClassName}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  )
}
